/**
 * Teams Redux Slice (workspace-scoped groups, assignable to projects/boards)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  Team,
  TeamMember,
  CreateTeamDto,
  UpdateTeamDto,
  AddTeamMemberDto,
  UpdateTeamMemberRoleDto,
} from '../../api/types/TeamTypes';
import { teamsApi } from '../../api/endpoints/teamsApi';

interface TeamsState {
  teamsByWorkspace: { [workspaceId: string]: Team[] };
  loading: boolean;
  error: string | null;
}

const initialState: TeamsState = {
  teamsByWorkspace: {},
  loading: false,
  error: null,
};

// ==================== Async Thunks ====================

export const fetchWorkspaceTeams = createAsyncThunk(
  'teams/fetchWorkspaceTeams',
  async (workspaceId: string) => {
    const teams = await teamsApi.getWorkspaceTeams(workspaceId);
    return { workspaceId, teams };
  },
);

export const createTeam = createAsyncThunk(
  'teams/createTeam',
  async ({ workspaceId, data }: { workspaceId: string; data: CreateTeamDto }) => {
    const team = await teamsApi.createTeam(workspaceId, data);
    return { workspaceId, team };
  },
);

export const updateTeam = createAsyncThunk(
  'teams/updateTeam',
  async ({
    workspaceId,
    teamId,
    data,
  }: {
    workspaceId: string;
    teamId: number;
    data: UpdateTeamDto;
  }) => {
    const team = await teamsApi.updateTeam(teamId, data);
    return { workspaceId, team };
  },
);

export const deleteTeam = createAsyncThunk(
  'teams/deleteTeam',
  async ({ workspaceId, teamId }: { workspaceId: string; teamId: number }) => {
    await teamsApi.deleteTeam(teamId);
    return { workspaceId, teamId };
  },
);

export const addTeamMember = createAsyncThunk(
  'teams/addTeamMember',
  async ({
    workspaceId,
    teamId,
    data,
  }: {
    workspaceId: string;
    teamId: number;
    data: AddTeamMemberDto;
  }) => {
    const member = await teamsApi.addMember(teamId, data);
    return { workspaceId, teamId, member };
  },
);

export const updateTeamMemberRole = createAsyncThunk(
  'teams/updateTeamMemberRole',
  async ({
    workspaceId,
    teamId,
    userId,
    data,
  }: {
    workspaceId: string;
    teamId: number;
    userId: number;
    data: UpdateTeamMemberRoleDto;
  }) => {
    const member = await teamsApi.updateMemberRole(teamId, userId, data);
    return { workspaceId, teamId, member };
  },
);

export const removeTeamMember = createAsyncThunk(
  'teams/removeTeamMember',
  async ({
    workspaceId,
    teamId,
    userId,
  }: {
    workspaceId: string;
    teamId: number;
    userId: number;
  }) => {
    await teamsApi.removeMember(teamId, userId);
    return { workspaceId, teamId, userId };
  },
);

// ==================== Slice ====================

const findTeam = (state: TeamsState, workspaceId: string, teamId: number) =>
  state.teamsByWorkspace[workspaceId]?.find((t) => t.id === teamId);

const teamsSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    clearErrors(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaceTeams.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkspaceTeams.fulfilled, (state, action) => {
        state.loading = false;
        const { workspaceId, teams } = action.payload;
        state.teamsByWorkspace[workspaceId] = teams;
      })
      .addCase(fetchWorkspaceTeams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch teams';
      });

    builder
      .addCase(createTeam.fulfilled, (state, action) => {
        const { workspaceId, team } = action.payload;
        if (!state.teamsByWorkspace[workspaceId]) {
          state.teamsByWorkspace[workspaceId] = [];
        }
        state.teamsByWorkspace[workspaceId].push(team);
      })
      .addCase(createTeam.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create team';
      });

    builder
      .addCase(updateTeam.fulfilled, (state, action) => {
        const { workspaceId, team } = action.payload;
        const list = state.teamsByWorkspace[workspaceId];
        if (list) {
          const index = list.findIndex((t) => t.id === team.id);
          if (index !== -1) list[index] = team;
        }
      })
      .addCase(updateTeam.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to rename team';
      });

    builder
      .addCase(deleteTeam.fulfilled, (state, action) => {
        const { workspaceId, teamId } = action.payload;
        const list = state.teamsByWorkspace[workspaceId];
        if (list) {
          state.teamsByWorkspace[workspaceId] = list.filter((t) => t.id !== teamId);
        }
      })
      .addCase(deleteTeam.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete team';
      });

    builder
      .addCase(addTeamMember.fulfilled, (state, action) => {
        const { workspaceId, teamId, member } = action.payload;
        const team = findTeam(state, workspaceId, teamId);
        if (team) {
          team.members = [...(team.members || []), member];
        }
      })
      .addCase(addTeamMember.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to add member';
      });

    builder
      .addCase(updateTeamMemberRole.fulfilled, (state, action) => {
        const { workspaceId, teamId, member } = action.payload;
        const team = findTeam(state, workspaceId, teamId);
        if (team && team.members) {
          const index = team.members.findIndex((m) => m.userId === member.userId);
          if (index !== -1) team.members[index] = member;
        }
      })
      .addCase(updateTeamMemberRole.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update member role';
      });

    builder
      .addCase(removeTeamMember.fulfilled, (state, action) => {
        const { workspaceId, teamId, userId } = action.payload;
        const team = findTeam(state, workspaceId, teamId);
        if (team && team.members) {
          team.members = team.members.filter((m) => m.userId !== userId);
        }
      })
      .addCase(removeTeamMember.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to remove member';
      });
  },
});

export const { clearErrors } = teamsSlice.actions;
export default teamsSlice.reducer;
